import { useState, useEffect, useMemo } from "react";
import { Menu, X, Sun, Moon, Globe, ChevronDown, ChevronUp } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom"; // React Router
import { Button } from "@/components/ui/button";
// import LogoutButton from "@/components/auth/LogoutButton";
import AuthButton from "@/components/auth/AuthButton";
// import { motion } from "framer-motion";

import { useGlobalLoading } from "@/context/LoadingContext";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next"; // Impor hook i18next

export function Navigation() {
  const { t, i18n } = useTranslation("navigation"); // Gunakan namespace 'nav'
  const { isLoading } = useGlobalLoading();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMoreOpen, setIsMoreOpen] = useState(false); // desktop
  const [isMoreMobileOpen, setIsMoreMobileOpen] = useState(false); // mobile
  const [isDark, setIsDark] = useState(false);
  // Bahasa sekarang diambil langsung dari i18n.language
  const [activeSection, setActiveSection] = useState<string>("home");
  const navigate = useNavigate();
  const location = useLocation();

  // Objek translations manual dihapus karena sudah dipindah ke file JSON

  // ✅ Gunakan useMemo agar tidak re-create di setiap render
  const menuItems = useMemo(
    () => [
      { href: "/#home", label: t("home") },
      { href: "/#about", label: t("about") },
      { href: "/#speakers", label: t("speakers") },
      { href: "/#conference-tracks", label: t("tracks") },
      { href: "/#timeline", label: t("timeline") },
      {
        href: "#more",
        label: t("more"),
        submenu: [
          { href: "/gallery", label: t("gallery") },
          { href: "/documents", label: t("documents") },
          { href: "https://go.undiksha.ac.id/BUKU_SAKU_KONASPIXII", label: t("pocketbook"), external: true },
          // { href: "/event-info", label: t.info },
        ],
      },
    ],
    [t] // hanya re-render ketika bahasa berubah (t berubah)
  );


  // IntersectionObserver untuk semua sections + submenu
  useEffect(() => {
    const sectionElements = menuItems.flatMap(item =>
      item.submenu
        ? item.submenu.map(sub => document.getElementById(sub.href.split("#")[1]))
        : document.getElementById(item.href.split("#")[1])
    ).filter(Boolean) as HTMLElement[];

    if (!sectionElements.length) return;

    // Deteksi section berdasarkan pita tipis tepat di bawah navbar (bukan persentase tinggi section),
    // supaya section yang sangat panjang (mis. Timeline) tetap terdeteksi aktif dengan benar.
    const observer = new IntersectionObserver((entries) => {
      let latestVisible = "";
      entries.forEach(entry => {
        if (entry.isIntersecting) latestVisible = entry.target.id;
      });
      if (latestVisible) setActiveSection(prev => prev !== latestVisible ? latestVisible : prev);
    }, { rootMargin: "-100px 0px -70% 0px", threshold: 0 });

    sectionElements.forEach(el => observer.observe(el));
    return () => sectionElements.forEach(el => observer.unobserve(el));
  }, [menuItems]);

  useEffect(() => {
    const path = location.pathname.replace("/", "");

    if (!path || path === "#home") {
      setActiveSection("home");
    } else {
      setActiveSection(path);
    }
  }, [location.pathname]);

  const handleMenuClick = (href: string, external?: boolean) => {
    setIsMenuOpen(false);
    setIsMoreOpen(false);
    setIsMoreMobileOpen(false);

    if (external) {
      window.open(href, '_blank', 'noopener,noreferrer');
      return;
    }

    if (href.startsWith("/")) {
      navigate(href);
      return;
    }

    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };


  const toggleLanguage = () => {
    const newLanguage = i18n.language === "id" ? "en" : "id";
    i18n.changeLanguage(newLanguage);
    // i18next-browser-languagedetector biasanya sudah menangani localStorage secara otomatis
  };


  useEffect(() => {
    const savedTheme = localStorage.getItem("theme");

    // Hanya aktifkan dark mode JIKA user pernah klik dan nyimpan "dark" di localStorage
    if (savedTheme === "dark") {
      setIsDark(true);
      document.documentElement.classList.add("dark");
    } else {
      // Default selalu light jika belum ada data di localStorage
      setIsDark(false);
      document.documentElement.classList.remove("dark");
    }
  }, []);

  // useEffect(() => {
  //   const savedTheme = localStorage.getItem("theme");
  //   const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;

  //   if (savedTheme === "dark" || (!savedTheme && prefersDark)) {
  //     setIsDark(true);
  //     document.documentElement.classList.add("dark");
  //   }

  //   // Pemuatan bahasa awal sudah ditangani oleh i18next-browser-languagedetector di i18n.ts
  // }, []);

  const toggleTheme = () => {
    setIsDark(!isDark);
    if (!isDark) {
      document.documentElement.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      document.documentElement.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
  };

  function scrollToHash(hash: string) {
    const element = document.querySelector(hash);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  }

  useEffect(() => {
    if (location.hash) {
      scrollToHash(location.hash);
    }
  }, [location]);

  return (
    <header className="border-b bg-background/80 backdrop-blur-sm sticky top-0 z-50">
      {/* Progress Bar */}
      <div
        className={cn(
          "absolute top-0 left-0 h-[3px] bg-blue-500 transition-all duration-300",
          isLoading ? "w-full opacity-100" : "w-0 opacity-0"
        )}
      />
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4 cursor-pointer" onClick={() => navigate("/")} >
            <div className="w-12 h-12 rounded-lg flex items-center justify-center">
              <img src={"/konaspi.png"} alt="Logo Konaspi" className="w-14 h-14 object-contain" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-foreground">{t("title")}</h1>
              <p className="text-sm text-muted-foreground">{t("subtitle")}</p>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-2">
            {menuItems.map(item => {
              const sectionId = item.href.split("#")[1];
              const isActive = activeSection === sectionId;

              if (item.submenu) {
                return (
                  <div key={item.label} className="relative">
                    <button
                      onClick={() => setIsMoreOpen(!isMoreOpen)}
                      className={`px-4 py-2 rounded-full transition-colors duration-300 flex items-center gap-1 ${isActive ? "bg-primary/3 text-primary" : "text-foreground hover:bg-muted hover:text-primary"}`}
                    >
                      {item.label} {isMoreOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    </button>
                    {isMoreOpen && (
                      <div className="absolute right-0 mt-2 w-50 bg-background border border-muted rounded-md shadow-lg flex flex-col">
                        {item.submenu.map(sub => {
                          const subId = sub.href.startsWith("/") ? sub.href.replace("/", "") : sub.href.split("#")[1];
                          return (
                            <button
                              key={sub.href}
                              onClick={() => handleMenuClick(sub.href, sub.external)}
                              className={`px-4 py-2 text-left transition-colors ${activeSection === subId
                                ? "bg-primary/3 text-primary"
                                : "text-foreground hover:bg-muted"
                                }`}
                            >
                              {sub.label}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )
              }

              return (
                <button key={item.href} onClick={() => handleMenuClick(item.href)}
                  className={`px-4 py-2 rounded-full transition-colors duration-300 ${isActive ? "bg-primary/3 text-primary" : "text-foreground hover:bg-muted hover:text-primary"}`}
                >
                  {item.label}
                </button>
              )
            })}
          </nav>

          {/* Theme Toggle and Mobile Menu Button */}
          <div className="flex items-center space-x-2">
            {/* Login Icon tetap tampil di mobile */}
            <div className="lg:hidden">
              <AuthButton checkAuth={false} isPublic={true} iconOnly={true} />
            </div>

            {/* Desktop: full auth button (jika ada) */}
            <div className="hidden lg:flex">
              <AuthButton checkAuth={false} isPublic={true} />
            </div>

            {/* Language Toggle – disembunyikan di mobile */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleLanguage}
              className="hidden lg:flex w-15 h-9 text-slate-700 hover:bg-slate-100 hover:text-slate-900 dark:hover:bg-slate-800 dark:hover:text-white"
            >
              <Globe className="h-4 w-4" />
              <span className="ml-1 text-xs font-medium ">{i18n.language.toUpperCase()}</span>
            </Button>

            {/* Theme Toggle – disembunyikan di mobile */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="hidden lg:flex w-9 h-9 text-slate-700 hover:bg-slate-100 hover:text-slate-900 dark:hover:bg-slate-800 dark:hover:text-white"
            >
              {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>

            {/* Mobile Menu */}
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden w-9 h-9 text-slate-700 hover:bg-slate-100 hover:text-slate-900 dark:hover:bg-slate-800 dark:hover:text-white"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>

        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t pt-4">
            <nav className="flex flex-col space-y-2">
              {menuItems.map(item => {
                const sectionId = item.href.split("#")[1];
                const isActive = activeSection === sectionId;

                if (item.submenu) {
                  return (
                    <div key={item.label} className="flex flex-col">
                      <button
                        onClick={() => setIsMoreMobileOpen(!isMoreMobileOpen)}
                        className={`px-4 py-2 rounded-full flex justify-between items-center text-left transition-colors ${isActive ? "bg-primary/3 text-primary" : "text-foreground hover:bg-muted hover:text-primary"
                          }`}
                      >
                        {item.label} {isMoreMobileOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>

                      {isMoreMobileOpen && (
                        <div className="flex flex-col pl-4 mt-1 space-y-1">
                          {item.submenu.map(sub => {
                            const subId = sub.href.startsWith("/") ? sub.href.replace("/", "") : sub.href.split("#")[1];
                            return (
                              <button
                                key={sub.href}
                                onClick={() => handleMenuClick(sub.href, sub.external)}
                                className={`px-4 py-2 text-left transition-colors ${activeSection === subId ? "bg-primary/3 text-primary" : "text-foreground hover:bg-muted"
                                  }`}
                              >
                                {sub.label}
                              </button>
                            );
                          })}
                        </div>
                      )}
                      {/* --- MORE MENU on MOBILE --- */}
                      <div className="mt-2 border-t pt-3 space-y-2">

                        {/* AuthButton */}
                        <div>
                          <AuthButton checkAuth={false} isPublic={true} />
                        </div>

                        {/* Language Toggle */}
                        <button
                          onClick={toggleLanguage}
                          className="flex items-center gap-2 px-4 py-2 rounded-md bg-muted hover:bg-muted/70 text-sm w-full"
                        >
                          <Globe className="h-4 w-4" />
                          {i18n.language === "id" ? "English" : "Bahasa Indonesia"}
                        </button>

                        {/* Theme Toggle */}
                        <button
                          onClick={toggleTheme}
                          className="flex items-center gap-2 px-4 py-2 rounded-md bg-muted hover:bg-muted/70 text-sm w-full"
                        >
                          {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                          {isDark ? "Light Mode" : "Dark Mode"}
                        </button>
                      </div>
                    </div>
                  );
                }

                return (
                  <button
                    key={item.href}
                    onClick={() => handleMenuClick(item.href)}
                    className={`px-4 py-2 rounded-full text-left transition-colors ${isActive ? "bg-primary/3 text-primary" : "text-foreground hover:bg-muted hover:text-primary"
                      }`}
                  >
                    {item.label}
                  </button>
                );
              })}

              {/* Fallback jika More tidak memiliki submenu (opsional) */}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}